import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
} from '@angular/core'
import { Dialog, DialogRef } from '@angular/cdk/dialog'
import { BehaviorSubject, Observable, Subject, take, takeUntil } from 'rxjs'
import {
    ViewerLayerNode,
    ViewerLayersService,
} from '../../../neuroglancer-frame/viewer-layers.service'
import { NestedTreeControl } from '@angular/cdk/tree'
import { ArrayDataSource } from '@angular/cdk/collections'
import { LayerGroupReference, LayerReference } from './layer-groups.model'
// import { GenericLayer } from 'src/app/shared/model/layers/generic-layer'
import { Store } from '@ngrx/store'
import { viewerActions } from '../../../../state/viewer/viewer.actions'
import { selectExpandedNodes, selectSearchKey } from '../../../../state/viewer/viewer.selectors'
import { GenericLayer } from 'src/app/shared/model/layers/generic-layer'
import { FavoriteLayersDialogComponent } from './favorite-layers-dialog/favorite-layers-dialog.component'
import { ViewerService } from '../../../neuroglancer-frame/viewer.service'

@Component({
    selector: 'app-layer-groups',
    templateUrl: './layer-groups.component.html',
    styleUrls: ['./layer-groups.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayerGroupsComponent implements OnDestroy {
    private _rawGroups: ViewerLayerNode[] = []

    @Input() set layerGroups(groups: ViewerLayerNode[]) {
        this._rawGroups = groups
        groups.forEach(g => sortChildren(g))
        this.applySearchFilter()

        function sortChildren(g: any) {
            if (!g.children) return
            g.children.forEach((c: any) => {
                if (c.children) {
                    sortChildren(c)
                }
            })
            g.children.sort((a: any, b: any) => {
                if (!a.children && !b.children) {
                    return a.layer?.name?.localeCompare(b.layer!.name!) || 0
                }
                if (!a.children) return -1
                if (!b.children) return 1

                return 0
            })
        }
    }

    private applySearchFilter(): void {
        this.store
            .select(selectSearchKey)
            .pipe(take(1))
            .subscribe(searchKey => {
                const filteredGroups = this._rawGroups.map(g => ({
                    ...g,
                    children: g.children?.filter(c =>
                        c.layer?.name?.toLowerCase().includes(searchKey.toLowerCase())
                    ),
                }))
                filteredGroups.forEach(group => this.restoreExpansionState(group))
                this._layers$.next(filteredGroups)
            })
    }

    restoreExpansionState = (group: ViewerLayerNode): void => {
        if (!!group?.id && this.expandedNodes.has(group.id)) {
            this.treeControl.expand(group)
        }
        group.children?.forEach(child => this.restoreExpansionState(child))
    }

    @Output() layerSelected = new EventEmitter<LayerReference>()

    @Output() layerGroupSelected = new EventEmitter<LayerGroupReference>()

    private _layers$: BehaviorSubject<ViewerLayerNode[]> = new BehaviorSubject([])

    treeControl = new NestedTreeControl<ViewerLayerNode>(node => node.children ?? [])
    dataSource = new ArrayDataSource(this._layers$)
    isLayer = (_: number, node: ViewerLayerNode): boolean => {
        return !!node.layer
    }
    isLayerGroupWithChildren = (_: number, node: { layerIds: string[] }): boolean =>
        !!node.layerIds && node.layerIds.length > 0

    expandedNodes: Set<string> = new Set()

    private readonly destroy$ = new Subject<void>()
    numOfVisibleLayersByGroup: { id: string; count: number }[] = []
    readonly searchKeyExists$ = this.store.select(selectSearchKey)
    readonly viewerConfigSyncInProgress$ = this.viewerService.viewerConfigSyncInProgress$

    constructor(
        private readonly viewerLayers: ViewerLayersService,
        readonly store: Store,
        private readonly dialog: Dialog,
        private readonly viewerService: ViewerService
    ) {
        this.store.select(selectExpandedNodes).subscribe(nodes => {
            this.expandedNodes = new Set(nodes)
        })
        this.store
            .select(selectSearchKey)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.applySearchFilter())
    }

    ngOnInit(): void {
        type ExtendedViewerLayerNode = {
            id?: string
            name: string
            layer?:
                | {
                      id: string
                      name: string
                      source: string
                      type: string
                      visible: boolean
                      visible$: Observable<boolean>
                  }
                | GenericLayer
                | null
            children?: ExtendedViewerLayerNode[] | null
            layerIds: string[]
        }
        this._layers$.subscribe(groups => {
            this.numOfVisibleLayersByGroup = []
            groups.forEach((g: ExtendedViewerLayerNode) => {
                reccurCountNumOfVisibleLayersByGroup(g, this.numOfVisibleLayersByGroup)
            })
        })
        function reccurCountNumOfVisibleLayersByGroup(
            g: ExtendedViewerLayerNode,
            numOfVisibleLayersByGroup: { id: string; count: number }[]
        ): void {
            let count = 0
            g.children?.forEach(gg => {
                if (!gg.children) {
                    gg.layer?.visible$?.subscribe(v => {
                        if (v) count++
                    })
                } else {
                    reccurCountNumOfVisibleLayersByGroup(gg, numOfVisibleLayersByGroup)
                }
            })
            numOfVisibleLayersByGroup.push({
                id: g.id || g.name,
                count: count,
            })
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    selectLayer($event: LayerReference): void {
        const { layer, elementId } = $event
        if (layer) this.layerSelected.emit({ layer, elementId })
    }

    selectLayerGroup($event: LayerGroupReference): void {
        const { groupName, groupId, elementId } = $event
        !!groupName && this.layerGroupSelected.emit({ groupName, groupId, elementId })
    }

    showAllLayers(groupId: string): void {
        this.store
            .select(selectSearchKey)
            .pipe(take(1))
            .subscribe(searchKey => {
                !!groupId &&
                    this.viewerLayers.getLayersForGroup(groupId).subscribe(layers => {
                        layers
                            .filter(l => l.name?.toLowerCase().includes(searchKey.toLowerCase()))
                            .forEach(layer => layer.setVisible(true))
                    })
            })
    }

    hideAllLayers(groupId: string): void {
        this.store
            .select(selectSearchKey)
            .pipe(take(1))
            .subscribe(searchKey => {
                !!groupId &&
                    this.viewerLayers.getLayersForGroup(groupId).subscribe(layers => {
                        layers
                            .filter(l => l.name?.toLowerCase().includes(searchKey.toLowerCase()))
                            .forEach(layer => layer.setVisible(false))
                    })
            })
    }

    numOfVisibleLayers(node: any): number {
        const layer = this.numOfVisibleLayersByGroup.find(g => g.id === node.id)
        return layer?.count || 0
    }

    toggleExpandBtn(id: string, isExpanded: boolean): void {
        if (isExpanded) {
            this.expandedNodes.add(id)
        } else {
            this.expandedNodes.delete(id)
        }
        this.store.dispatch(
            viewerActions.setExpandedNodes({ expandedNodes: [...this.expandedNodes] })
        )
    }

    openLayerList(groupId: string, initialLoadStatusFilter: 'all' | 'loaded' | 'unloaded' = 'all'): void {
        if (this.viewerService.isConfigSyncInProgress()) {
            return
        }

        const scrollContainer = document.getElementById('layers-pane-scroll')
        const scrollTop = scrollContainer?.scrollTop ?? 0

        this.store.dispatch(viewerActions.setLayerListDialogOpen({ open: true }))

        const dialogRef: DialogRef<boolean | undefined> = this.dialog.open(
            FavoriteLayersDialogComponent,
            {
                data: { groupId, initialLoadStatusFilter },
                restoreFocus: false,
            }
        )
        dialogRef.closed.pipe(take(1)).subscribe(layersChanged => {
            this.store.dispatch(
                viewerActions.setLayerListDialogOpen({
                    open: false,
                    layersChanged: !!layersChanged,
                })
            )
            if (layersChanged) {
                this.store.dispatch(viewerActions.syncViewerConfig({}))
            }
            if (scrollContainer) {
                requestAnimationFrame(() => {
                    scrollContainer.scrollTop = scrollTop
                })
            }
        })
    }
}
